import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useUserStore } from "@/store/userStore";
import { useAuth } from "@/contexts/AuthContext";
import { checkSuperAdmin } from "@/services/edgeFunctionWrappers/userRolesWrapper";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface SuperAdminGuardProps {
  children: React.ReactNode;
}

export function SuperAdminGuard({ children }: SuperAdminGuardProps) {
  const { t } = useTranslation(["common"]);
  const setRole = useUserStore((s) => s.setRole);
  // On lit l'utilisateur depuis le contexte d'authentification, pas depuis le
  // store. Deux raisons :
  //  - le store n'est alimenté que par un effet de `AuthProvider`, qui
  //    s'exécute APRÈS les effets des enfants : au premier rendu où `loading`
  //    passe à `false`, `useUserStore().user` valait encore `null` ;
  //  - sans attendre `loading`, ce garde redirigeait vers /login à chaque
  //    rechargement d'une page `/super-admin/*` (audit lot 7).
  const { user, loading: isAuthLoading } = useAuth();
  const [isCheckingRole, setIsCheckingRole] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    async function checkSuperAdminRole() {
      if (isAuthLoading) {
        // Session pas encore résolue : on ne conclut rien.
        setIsCheckingRole(true);
        return;
      }

      if (!user) {
        setIsCheckingRole(false);
        return;
      }

      try {
        // Verify valid session exists before checking role
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          console.log('SuperAdminGuard: No valid session, user not super admin');
          setIsSuperAdmin(false);
          setIsCheckingRole(false);
          return;
        }

        const isSuperAdminResult = await checkSuperAdmin(user.id);

        setIsSuperAdmin(isSuperAdminResult);
        if (isSuperAdminResult) {
          setRole('super_admin');
        }
      } catch (error) {
        console.error('Error checking super admin role:', error);
        setIsSuperAdmin(false);
      } finally {
        setIsCheckingRole(false);
      }
    }

    checkSuperAdminRole();
  }, [user, setRole, isAuthLoading]);

  if (isAuthLoading || isCheckingRole) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">{t("checkingPermissions", { ns: "common", defaultValue: "Vérification des permissions..." })}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isSuperAdmin) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
